import React from "react";
import { Card, CardBody, CardHeader, ListGroup, ListGroupItem } from "reactstrap";
import Select from "react-select";

export type InventoryFilterOption = {
  value: string;
  label: string;
};

export type InventoryCategory = {
  value: string;
  label: string;
  count?: number;
};

export type InventoryExtraSelect = {
  id: string;
  label: string;
  options: InventoryFilterOption[];
  selected: readonly InventoryFilterOption[];
  onChange: (options: readonly InventoryFilterOption[]) => void;
};

interface InventoryFiltersPanelProps {
  title?: string;
  selectedOptions: readonly InventoryFilterOption[];
  onSelectedOptionsChange: (options: readonly InventoryFilterOption[]) => void;
  options: InventoryFilterOption[];
  categories: InventoryCategory[];
  activeCategory: string;
  onCategoryChange: (category: string) => void;
  onClear?: () => void;
  disabled?: boolean;
  extraSelects?: InventoryExtraSelect[];
  headerExtras?: React.ReactNode;
}

const InventoryFiltersPanel = ({
  title = "Filtros",
  selectedOptions,
  onSelectedOptionsChange,
  options,
  categories,
  activeCategory,
  onCategoryChange,
  onClear,
  disabled = false,
  extraSelects = [],
  headerExtras,
}: InventoryFiltersPanelProps) => {
  const handleSelectChange = (selected: readonly InventoryFilterOption[] | null) => {
    onSelectedOptionsChange(selected ?? []);
  };

  const handleCategoryClick = (value: string) => {
    onCategoryChange(value);
  };

  return (
    <Card>
      <CardHeader>
        {headerExtras ? <div className="mb-3">{headerExtras}</div> : null}
        <div className="d-flex align-items-center mb-3">
          <div className="flex-grow-1">
            <h5 className="fs-16 mb-0">{title}</h5>
          </div>
          {onClear ? (
            <div className="flex-shrink-0">
              <button
                type="button"
                className="btn btn-link text-decoration-underline p-0"
                onClick={onClear}
              >
                Limpiar
              </button>
            </div>
          ) : null}
        </div>
        <p className="text-muted text-uppercase fs-12 fw-medium mb-2">Depósitos</p>
        <ListGroup flush className="filter-list mb-3">
          {categories.map((category) => (
            <ListGroupItem
              key={category.value}
              tag="button"
              action
              disabled={disabled}
              onClick={() => handleCategoryClick(category.value)}
              className={`d-flex justify-content-between align-items-center ${
                activeCategory === category.value ? "active" : ""
              }`}
            >
              <span>{category.label}</span>
              {typeof category.count === "number" ? (
                <span className="badge bg-light text-muted">{category.count}</span>
              ) : null}
            </ListGroupItem>
          ))}
        </ListGroup>
        <div className="filter-choices-input">
          <Select<InventoryFilterOption, true>
            value={selectedOptions}
            isMulti
            options={options}
            onChange={handleSelectChange}
            classNamePrefix="select2-selection"
            isDisabled={disabled}
            placeholder="Filtrar etiquetas"
          />
        </div>
        {extraSelects.map((selectItem) => (
          <div className="mt-3" key={selectItem.id}>
            <div className="text-muted text-uppercase fs-12 fw-medium mb-2">{selectItem.label}</div>
            <Select<InventoryFilterOption, true>
              value={selectItem.selected}
              isMulti
              options={selectItem.options}
              onChange={(selected: readonly InventoryFilterOption[] | null) =>
                selectItem.onChange(selected ?? [])
              }
              classNamePrefix="select2-selection"
              isDisabled={disabled}
              placeholder={`Seleccionar ${selectItem.label.toLowerCase()}`}
            />
          </div>
        ))}
      </CardHeader>
      <CardBody></CardBody>
    </Card>
  );
};

export default InventoryFiltersPanel;
